import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useDonationDetails } from '@hooks/register/screening/use-donation-details';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { asString, asStringBool } from '@/utils/parse';
import { createDonationDetailsSchema } from '@views/register/screening/donation-details.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IDonationDetailsFormValues, IDonationDetailsModel, IDonationDetailsSubmitResult } from '@/types/screening/donation-details';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const DonationDetails = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useDonationDetails(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <DonationDetailsContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IDonationDetailsContentProps {
    model: IDonationDetailsModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IDonationDetailsFormValues) => Promise<IDonationDetailsSubmitResult>;
}

const DonationDetailsContent = ({ model, isSubmitting, submitError, onSubmit }: IDonationDetailsContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IDonationDetailsFormValues, IDonationDetailsSubmitResult>({
        resolver: zodResolver(createDonationDetailsSchema(model)),
        defaultValues: {
            hasIntendedRecipient: asStringBool(model.hasIntendedRecipient),
            ddRelationshipToPatient: asString(model.ddRelationshipToPatient),
            ddRecipFirstName: model.ddRecipFirstName,
            ddRecipLastName: model.ddRecipLastName,
            ddKnowsRecipDob: asStringBool(model.ddKnowsRecipDob),
            ddRecipDob: model.ddRecipDob?.slice(0, 10) ?? '',
            recipientCenterId: asString(model.recipientCenterId),
            recipientAtSameDashCenter: asStringBool(model.recipientAtSameDashCenter),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        watch,
        setValue,
        formState: { errors },
    } = form;

    // #dd shows when the donor has a specific intended recipient.
    const showDirected = watch('hasIntendedRecipient') === '1';
    const showRecipDob = watch('ddKnowsRecipDob') === '1';
    // Private-label directed donors answer "at our center?"; No reveals the center select.
    const showCenterSelect = !model.isReferral && watch('recipientAtSameDashCenter') === '0';

    useEffect(() => {
        if (!showDirected) {
            setValue('ddRelationshipToPatient', '');
            setValue('ddRecipFirstName', '');
            setValue('ddRecipLastName', '');
            setValue('ddKnowsRecipDob', '');
            setValue('ddRecipDob', '');
            setValue('recipientCenterId', '');
            setValue('recipientAtSameDashCenter', '');
        }
    }, [showDirected, setValue]);

    useEffect(() => {
        if (!showRecipDob) {
            setValue('ddRecipDob', '');
        }
    }, [showRecipDob, setValue]);

    // Group the center options by state (order preserved from the server).
    const centerGroups: { group: string; options: typeof model.recipientCenterOptions }[] = [];

    model.recipientCenterOptions.forEach((option) => {
        let group = centerGroups.find((g) => g.group === option.group);
        if (!group) {
            group = { group: option.group, options: [] };
            centerGroups.push(group);
        }
        group.options.push(option);
    });

    const centerSelect = (
        <select className="form-control" {...register('recipientCenterId')}>
            <option value="">{t('DashLabel_PleaseSelect', 'Please Select')}</option>
            {centerGroups.map((group) => (
                <optgroup key={group.group} label={group.group}>
                    {group.options.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.name}
                        </option>
                    ))}
                </optgroup>
            ))}
        </select>
    );

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.DonationDetails}
            title={t('DashLabel_DonationDetails', 'Your Donation')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_WhatBestDescribesYourCurrentSituation',
                        'Who do you want to help with your donation',
                    )}
                    ?<FieldError message={errors.hasIntendedRecipient?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <div className="radio">
                        <label>
                            <input
                                type="radio"
                                value="0"
                                {...register('hasIntendedRecipient')}
                            />{' '}
                            {t(
                                'DashLabel_IDoNotHaveASpecificPersonThatIWantToHelpIWantToDonate',
                                'Anyone in need',
                            )}
                            .
                        </label>
                    </div>
                    <div className="radio">
                        <label>
                            <input
                                type="radio"
                                value="1"
                                {...register('hasIntendedRecipient')}
                            />{' '}
                            {t(
                                'DashLabel_IHaveASpecificPersonThatIWantToHelpByDonating',
                                'I have a specific person that I want to help by donating',
                            )}
                            .
                        </label>
                    </div>
                </div>
            </div>

            {showDirected && (
                <div className="sub-question" id="dd">
                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="DdRelationshipToPatient">
                            {t('DashLabel_WhoDoYouWantToHelp', 'Who do you want to help')}?
                            <FieldError message={errors.ddRelationshipToPatient?.message} />
                        </label>
                        <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                            <select
                                id="DdRelationshipToPatient"
                                className="form-control"
                                {...register('ddRelationshipToPatient')}
                            >
                                <option value="">
                                    {t('DashLabel_PleaseSelect', 'Please Select')}
                                </option>
                                {model.relationshipOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="DdRecipFirstName">
                            {t('DashLabel_FirstName', 'First Name')}:
                            <FieldError message={errors.ddRecipFirstName?.message} />
                        </label>
                        <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                            <input
                                id="DdRecipFirstName"
                                className="form-control"
                                maxLength={32}
                                {...register('ddRecipFirstName')}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="DdRecipLastName">
                            {t('DashLabel_LastName', 'Last Name')}:
                            <FieldError message={errors.ddRecipLastName?.message} />
                        </label>
                        <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                            <input
                                id="DdRecipLastName"
                                className="form-control"
                                maxLength={32}
                                {...register('ddRecipLastName')}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="DdKnowsRecipDob">
                            {t(
                                'DashLabel_DoYouKnowThePersonsBirthDate',
                                "Do you know the person's birth date",
                            )}
                            ?<FieldError message={errors.ddKnowsRecipDob?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="1"
                                    {...register('ddKnowsRecipDob')}
                                />{' '}
                                {t('Word_Yes', 'Yes')}
                            </label>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="0"
                                    {...register('ddKnowsRecipDob')}
                                />{' '}
                                {t('Word_No', 'No')}
                            </label>
                        </div>
                    </div>

                    {showRecipDob && (
                        <div className="form-group" id="recipDob">
                            <label className="control-label col-xs-12" htmlFor="DdRecipDob">
                                {t('DashLabel_PersonsBirthDate', "Persons's Birth Date")}:
                                <FieldError message={errors.ddRecipDob?.message} />
                            </label>
                            <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                                <input
                                    id="DdRecipDob"
                                    type="date"
                                    className="form-control"
                                    {...register('ddRecipDob')}
                                />
                            </div>
                        </div>
                    )}

                    {model.isReferral ? (
                        <div className="form-group">
                            <label className="control-label col-xs-12" htmlFor="RecipientCenterId">
                                {t(
                                    'DashLabel_WhichTransplantCenterIsThePersonYouWantToHelpAt',
                                    'Which transplant center is the person you want to help at',
                                )}
                                ?<FieldError message={errors.recipientCenterId?.message} />
                            </label>
                            <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                                {centerSelect}
                            </div>
                        </div>
                    ) : (
                        !model.isLiver && (
                            <>
                                <div className="form-group">
                                    <label className="control-label col-xs-12">
                                        {t(
                                            'DashLabel_IsYourRecipientCurrentlyAtOurTransplantCenter',
                                            "Is the person you're donating for currently at our transplant center",
                                        )}
                                        ?
                                        <FieldError
                                            message={errors.recipientAtSameDashCenter?.message}
                                        />
                                    </label>
                                    <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                        <label className="radio-inline">
                                            <input
                                                type="radio"
                                                value="1"
                                                {...register('recipientAtSameDashCenter')}
                                            />{' '}
                                            {t('Word_Yes', 'Yes')}
                                        </label>
                                        <label className="radio-inline">
                                            <input
                                                type="radio"
                                                value="0"
                                                {...register('recipientAtSameDashCenter')}
                                            />{' '}
                                            {t('Word_No', 'No')}
                                        </label>
                                    </div>
                                </div>

                                {showCenterSelect && (
                                    <div className="form-group" id="centerSelect">
                                        <label
                                            className="control-label col-xs-12"
                                            htmlFor="RecipientCenterId"
                                        >
                                            {t(
                                                'DashLabel_WhichTransplantCenterIsThePersonYouWantToHelpAt',
                                                'Which transplant center is the person you want to help at',
                                            )}
                                            ?
                                            <FieldError
                                                message={errors.recipientCenterId?.message}
                                            />
                                        </label>
                                        <div
                                            className="col-xs-12 col-sm-8"
                                            style={{ marginLeft: 10 }}
                                        >
                                            {centerSelect}
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    )}
                </div>
            )}
        </StepFormLayout>
    );
};

export default DonationDetails;
